import { useCallback, useEffect, useMemo, useState } from "react";
import {
  App,
  Badge,
  Button,
  Card,
  Descriptions,
  Radio,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { adminFetch, errorMessage } from "../../lib/adminApi";
import { ActiveSubscriptionSwitch } from "../../components/ActiveSubscriptionSwitch";
import type {
  BankTransferPurchase,
  BankTransferPurchasesResponse,
  PurchaseSettlementStatus,
  SettlePurchaseResponse,
} from "../../types/purchases";
import { formatAmount, intervalLabel } from "../../types/purchases";

const { Title, Paragraph, Text } = Typography;

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const normalized = value.replace(/(\.\d{3})\d+/, "$1");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("pl-PL");
}

function planLabel(purchase: BankTransferPurchase): string {
  if (!purchase.plan_name) return purchase.stripe_price_id ?? "—";
  const interval = intervalLabel(purchase.plan_interval);
  return interval ? `${purchase.plan_name} (${interval})` : purchase.plan_name;
}

function clientLabel(purchase: BankTransferPurchase): string {
  return purchase.participant_name || purchase.user_email || purchase.participant_id;
}

export const PurchasesList = () => {
  const { notification, modal } = App.useApp();
  const [status, setStatus] = useState<PurchaseSettlementStatus>("pending");
  const [activeOnly, setActiveOnly] = useState(false);
  const [purchases, setPurchases] = useState<BankTransferPurchase[]>([]);
  const [loading, setLoading] = useState(false);
  const [settlingId, setSettlingId] = useState<string | null>(null);

  const load = useCallback(
    async (settlementStatus: PurchaseSettlementStatus, onlyActive: boolean) => {
      setLoading(true);
      try {
        const response = await adminFetch<BankTransferPurchasesResponse>(
          `purchases?status=${settlementStatus}${onlyActive ? "&active_only=true" : ""}`,
        );
        setPurchases(response.data ?? []);
      } catch (error) {
        setPurchases([]);
        notification.error({
          message: "Nie udało się pobrać wpłat przelewem",
          description: errorMessage(error),
        });
      } finally {
        setLoading(false);
      }
    },
    [notification],
  );

  useEffect(() => {
    void load(status, activeOnly);
  }, [status, activeOnly, load]);

  const settle = useCallback(
    async (purchase: BankTransferPurchase) => {
      setSettlingId(purchase.id);
      try {
        const response = await adminFetch<SettlePurchaseResponse>(
          `purchases/${purchase.id}/settle`,
          { method: "POST" },
        );
        const result = response.data;
        if (result.already_settled) {
          notification.info({
            message: "Wpłata była już rozliczona",
            description: "Subskrypcja nie została zdublowana.",
          });
        } else {
          notification.success({
            message: result.created_subscription
              ? "Wpłata rozliczona — utworzono subskrypcję"
              : "Wpłata rozliczona — przedłużono subskrypcję",
            description: `Okres: ${formatDate(result.start_date)} → ${formatDate(result.end_date)}`,
          });
        }
        await load(status, activeOnly);
      } catch (error) {
        notification.error({
          message: "Nie udało się rozliczyć wpłaty",
          description: errorMessage(error),
        });
      } finally {
        setSettlingId(null);
      }
    },
    [activeOnly, load, notification, status],
  );

  const confirmSettle = useCallback(
    (purchase: BankTransferPurchase) => {
      modal.confirm({
        title: "Oznaczyć wpłatę jako otrzymaną?",
        width: 560,
        content: (
          <div>
            <Paragraph>
              Potwierdź, że przelew <b>faktycznie wpłynął na konto</b>. Ta akcja aktywuje lub
              przedłuża subskrypcję klienta. Operacja jest idempotentna — powtórne kliknięcie nie
              zdubluje okresu.
            </Paragraph>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Klient">{clientLabel(purchase)}</Descriptions.Item>
              <Descriptions.Item label="Proforma">{purchase.pretty_id ?? "—"}</Descriptions.Item>
              <Descriptions.Item label="Plan">{planLabel(purchase)}</Descriptions.Item>
              <Descriptions.Item label="Oczekiwana kwota">{formatAmount(purchase)}</Descriptions.Item>
            </Descriptions>
          </div>
        ),
        okText: "Wpłata wpłynęła — aktywuj",
        cancelText: "Anuluj",
        onOk: () => settle(purchase),
      });
    },
    [modal, settle],
  );

  const showActions = status !== "settled";

  const columns = useMemo(
    () => [
      {
        title: "Klient",
        key: "client",
        render: (_: unknown, record: BankTransferPurchase) => (
          <Space direction="vertical" size={0}>
            <Text strong>{record.participant_name || "—"}</Text>
            {record.user_email && <Text type="secondary">{record.user_email}</Text>}
          </Space>
        ),
      },
      {
        title: "Proforma",
        dataIndex: "pretty_id",
        key: "pretty_id",
        render: (value: string | null) => value ?? "—",
      },
      {
        title: "Plan",
        key: "plan",
        render: (_: unknown, record: BankTransferPurchase) => planLabel(record),
      },
      {
        title: "Oczekiwana kwota",
        key: "amount",
        render: (_: unknown, record: BankTransferPurchase) => formatAmount(record),
      },
      {
        title: "Utworzono",
        dataIndex: "created_at",
        key: "created_at",
        render: (value: string) => formatDate(value),
      },
      {
        title: "Deklaracja usera",
        dataIndex: "confirmed",
        key: "confirmed",
        render: (confirmed: boolean) =>
          confirmed ? (
            <Tag color="blue">potwierdził przelew</Tag>
          ) : (
            <Tag>brak deklaracji</Tag>
          ),
      },
      {
        title: "Stan",
        key: "state",
        render: (_: unknown, record: BankTransferPurchase) =>
          record.settled_at ? (
            <Space direction="vertical" size={0}>
              <Badge status="success" text="Rozliczona" />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {formatDate(record.settled_at)}
                {record.settled_by_email ? ` · ${record.settled_by_email}` : ""}
              </Text>
            </Space>
          ) : (
            <Badge status="warning" text="Do rozliczenia" />
          ),
      },
      ...(showActions
        ? [
            {
              title: "Akcje",
              key: "actions",
              render: (_: unknown, record: BankTransferPurchase) =>
                record.settled_at ? (
                  <Text type="secondary">—</Text>
                ) : (
                  <Button
                    type="primary"
                    size="small"
                    loading={settlingId === record.id}
                    onClick={() => confirmSettle(record)}
                  >
                    Oznacz jako otrzymaną
                  </Button>
                ),
            },
          ]
        : []),
    ],
    [confirmSettle, settlingId, showActions],
  );

  return (
    <Card>
      <Title level={4}>Wpłaty przelewem</Title>
      <Paragraph type="secondary">
        Wpłaty za subskrypcję opłacane przelewem bankowym. Po potwierdzeniu wpływu pieniędzy na
        konto oznacz wpłatę jako otrzymaną — subskrypcja klienta zostanie aktywowana lub przedłużona
        zgodnie z planem z proformy.
      </Paragraph>

      <Space wrap size={16} style={{ marginBottom: 16 }}>
        <Radio.Group
          value={status}
          onChange={(event) => setStatus(event.target.value as PurchaseSettlementStatus)}
        >
          <Radio.Button value="pending">Do rozliczenia</Radio.Button>
          <Radio.Button value="settled">Rozliczone</Radio.Button>
          <Radio.Button value="all">Wszystkie</Radio.Button>
        </Radio.Group>
        <ActiveSubscriptionSwitch
          checked={activeOnly}
          onChange={setActiveOnly}
          label="Tylko firmy z aktywną subskrypcją"
          scope="company"
        />
      </Space>

      <Table<BankTransferPurchase>
        rowKey="id"
        dataSource={purchases}
        columns={columns}
        loading={loading}
        pagination={false}
        scroll={{ x: true }}
        locale={{
          emptyText:
            status === "pending"
              ? "Brak wpłat do rozliczenia"
              : "Brak wpłat dla wybranego filtra",
        }}
      />
    </Card>
  );
};
